(() => {
  const inventory = window.KronkCollectionQuickAddInventory || {
    lowStockLimit: 3,

    getNumber(value, fallback = 0) {
      const number = parseInt(value, 10);
      return Number.isNaN(number) ? fallback : number;
    },

    getVariantButtons(variantId) {
      return Array.from(document.querySelectorAll('[data-kronk-variant-id]')).filter(
        (button) => String(button.dataset.kronkVariantId) === String(variantId)
      );
    },

    getSubmittedQuantity(form) {
      const quantityInput = form?.querySelector('[name="quantity"]');
      return this.getNumber(quantityInput?.value, 1);
    },

    getLowStockText(remaining) {
      return remaining === 1 ? 'Only 1 left' : `Only ${remaining} left`;
    },

    setStockLabel(button, text = '') {
      const stockLabel = button.querySelector('[data-kronk-stock-label]');
      if (!stockLabel) return;

      stockLabel.textContent = text;
      stockLabel.hidden = text.length === 0;
    },

    setActionLabel(button, text) {
      const mobileAction = button.querySelector('[data-kronk-action-label]');
      const desktopLabel = button.querySelector('[data-kronk-button-label]');

      if (mobileAction) mobileAction.textContent = text;
      if (desktopLabel && button.classList.contains('kronk-card-variants__button')) desktopLabel.textContent = text;
    },

    setAvailable(button, remaining) {
      const defaultLabel = button.dataset.kronkDefaultLabel || 'Add';

      button.disabled = false;
      button.removeAttribute('aria-disabled');
      button.classList.remove('is-disabled', 'is-all-in-cart');
      this.setActionLabel(button, button.querySelector('[data-kronk-action-label]') ? 'Add' : defaultLabel);

      if (button.dataset.kronkInventoryTracked === 'true' && remaining <= this.lowStockLimit) {
        button.classList.add('is-low-stock');
        this.setStockLabel(button, this.getLowStockText(remaining));
      } else {
        button.classList.remove('is-low-stock');
        this.setStockLabel(button);
      }
    },

    setAllInCart(button) {
      button.disabled = true;
      button.classList.add('is-disabled', 'is-all-in-cart');
      button.classList.remove('is-low-stock');
      button.dataset.kronkInventoryRemaining = '0';
      this.setActionLabel(button, button.querySelector('[data-kronk-action-label]') ? 'All in cart' : 'In cart');
      this.setStockLabel(button, 'All available inventory is in your cart');
    },

    setOutOfStock(button) {
      const defaultLabel = button.dataset.kronkDefaultLabel || '';

      button.disabled = true;
      button.classList.add('is-disabled');
      button.classList.remove('is-all-in-cart', 'is-low-stock');
      button.dataset.kronkInventoryRemaining = '0';
      this.setActionLabel(button, button.querySelector('[data-kronk-action-label]') ? 'Out of stock' : defaultLabel);
      this.setStockLabel(button);
    },

    updateFromRemaining(button, remaining) {
      if (!button || button.dataset.kronkBaseAvailable !== 'true') return;

      const minimum = this.getNumber(button.dataset.kronkQuantityMin, 1);
      const safeRemaining = Math.max(this.getNumber(remaining, 0), 0);

      button.dataset.kronkInventoryRemaining = String(safeRemaining);

      if (safeRemaining < minimum) {
        this.setAllInCart(button);
      } else {
        this.setAvailable(button, safeRemaining);
      }
    },

    decreaseVariant(variantId, quantity = 1) {
      this.getVariantButtons(variantId).forEach((button) => {
        if (button.dataset.kronkInventoryTracked !== 'true') return;

        const remaining = this.getNumber(button.dataset.kronkInventoryRemaining, 0);
        this.updateFromRemaining(button, remaining - quantity);
      });
    },

    markVariantAllInCart(variantId) {
      this.getVariantButtons(variantId).forEach((button) => this.setAllInCart(button));
    },

    markVariantOutOfStock(variantId) {
      this.getVariantButtons(variantId).forEach((button) => this.setOutOfStock(button));
    },

    syncFromCartData(cartData) {
      if (!cartData || !Array.isArray(cartData.items)) return;

      const quantitiesByVariant = new Map();
      cartData.items.forEach((item) => {
        const variantId = String(item.variant_id || item.id);
        quantitiesByVariant.set(variantId, (quantitiesByVariant.get(variantId) || 0) + item.quantity);
      });

      document.querySelectorAll('[data-kronk-variant-id][data-kronk-inventory-tracked="true"]').forEach((button) => {
        if (button.dataset.kronkBaseAvailable !== 'true') return;

        const total = this.getNumber(button.dataset.kronkInventoryTotal, NaN);
        if (Number.isNaN(total)) return;

        const cartQuantity = quantitiesByVariant.get(String(button.dataset.kronkVariantId)) || 0;
        this.updateFromRemaining(button, total - cartQuantity);
      });
    },

    getRawMessage(event) {
      const rawMessage = event?.errors || event?.message || 'That size could not be added. Please try another size.';

      if (Array.isArray(rawMessage)) return rawMessage.filter(Boolean).join(' ');
      if (typeof rawMessage === 'object') return Object.values(rawMessage).flat().filter(Boolean).join(' ');

      return rawMessage;
    },

    isAllInCartMessage(message) {
      return /maximum quantity|already in your cart|can't add more|cannot add more/i.test(message);
    },

    isSoldOutMessage(message) {
      return /sold out|unavailable|not available/i.test(message);
    },

    getFriendlyMessage(message) {
      if (this.isAllInCartMessage(message)) return 'All available inventory for this size is already in your cart.';
      if (this.isSoldOutMessage(message)) return 'That size just sold out.';

      return message || 'That size could not be added. Please try another size.';
    },
  };

  window.KronkCollectionQuickAddInventory = inventory;

  if (!window.KronkCollectionQuickAddInventorySubscribed && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
    window.KronkCollectionQuickAddInventorySubscribed = true;
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      inventory.syncFromCartData(event?.cartData);
    });
  }

  if (!customElements.get('kronk-mobile-quick-add')) {
    customElements.define(
      'kronk-mobile-quick-add',
      class KronkMobileQuickAdd extends HTMLElement {
        constructor() {
          super();
          this.onKeyDown = this.onKeyDown.bind(this);
          this.onSubmit = this.onSubmit.bind(this);
          this.onCartUpdate = this.onCartUpdate.bind(this);
          this.onCartError = this.onCartError.bind(this);
          this.close = this.close.bind(this);
        }

        connectedCallback() {
          this.trigger = this.querySelector('.kronk-mobile-quick-add__trigger');
          this.sheet = this.querySelector('.kronk-mobile-quick-add__sheet');
          this.overlay = this.querySelector('.kronk-mobile-quick-add__overlay');
          this.message = this.querySelector('.kronk-mobile-quick-add__message');
          this.closeTimer = null;
          this.pendingVariantId = null;
          this.pendingButton = null;
          this.pendingQuantity = 1;

          if (!this.trigger || !this.sheet || !this.overlay) return;

          this.closeControls = [this.overlay, ...this.sheet.querySelectorAll('[data-kronk-quick-add-close]')];
          this.trigger.addEventListener('click', () => this.open());
          this.closeControls.forEach((control) => control.addEventListener('click', () => this.close()));
          this.sheet.addEventListener('submit', this.onSubmit);

          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
            this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
            this.unsubscribeCartError = subscribe(PUB_SUB_EVENTS.cartError, this.onCartError);
          }
        }

        disconnectedCallback() {
          document.removeEventListener('keydown', this.onKeyDown);
          if (this.unsubscribeCartUpdate) this.unsubscribeCartUpdate();
          if (this.unsubscribeCartError) this.unsubscribeCartError();
          if (this.portal) this.portal.remove();
        }

        ensurePortal() {
          if (this.portal) return;

          this.portal = document.createElement('div');
          this.portal.className = this.classList.contains('kronk-cart-upsell-mobile-quick-add')
            ? 'kronk-collection-grid kronk-mobile-quick-add-portal kronk-cart-upsell-mobile-quick-add-portal'
            : 'kronk-collection-grid kronk-mobile-quick-add-portal';
          this.appendChild(document.createComment('KRONK mobile quick-add sheet moved to body'));
          this.portal.append(this.overlay, this.sheet);
          document.body.appendChild(this.portal);
        }

        open() {
          if (this.trigger.disabled || this.classList.contains('is-open')) return;

          document.querySelectorAll('kronk-mobile-quick-add.is-open').forEach((sheet) => {
            if (sheet !== this && typeof sheet.close === 'function') sheet.close({ restoreFocus: false });
          });

          this.ensurePortal();
          window.clearTimeout(this.closeTimer);
          this.overlay.hidden = false;
          this.sheet.hidden = false;
          this.trigger.setAttribute('aria-expanded', 'true');
          document.documentElement.classList.add('kronk-quick-add-open');
          document.addEventListener('keydown', this.onKeyDown);

          window.requestAnimationFrame(() => {
            this.classList.add('is-open');
            this.portal.classList.add('is-open');
            const closeButton = this.sheet.querySelector('.kronk-mobile-quick-add__close');
            if (closeButton) closeButton.focus({ preventScroll: true });
          });
        }

        close(options = {}) {
          const { restoreFocus = true } = options;
          if (!this.classList.contains('is-open') && this.sheet.hidden) return;

          this.classList.remove('is-open');
          this.portal.classList.remove('is-open');
          this.trigger.setAttribute('aria-expanded', 'false');
          document.removeEventListener('keydown', this.onKeyDown);

          this.closeTimer = window.setTimeout(() => {
            if (!this.classList.contains('is-open')) {
              this.overlay.hidden = true;
              this.sheet.hidden = true;
            }
          }, 220);

          if (!document.querySelector('kronk-mobile-quick-add.is-open')) {
            document.documentElement.classList.remove('kronk-quick-add-open');
          }

          if (restoreFocus) this.trigger.focus({ preventScroll: true });
        }

        onSubmit(event) {
          const form = event.target;
          const variantInput = form.querySelector('[name="id"]');

          this.pendingVariantId = variantInput ? variantInput.value : null;
          this.pendingButton = event.submitter || form.querySelector('[type="submit"]');
          this.pendingQuantity = inventory.getSubmittedQuantity(form);
          this.clearMessage();
        }

        onCartUpdate(event) {
          if (!this.isPendingEvent(event)) return;

          inventory.decreaseVariant(this.pendingVariantId, this.pendingQuantity);
          this.clearPendingState();
          window.setTimeout(() => this.close({ restoreFocus: false }), 50);
        }

        onCartError(event) {
          if (!this.isPendingEvent(event)) return;

          const rawMessage = inventory.getRawMessage(event);
          const message = inventory.getFriendlyMessage(rawMessage);

          if (inventory.isAllInCartMessage(rawMessage)) {
            inventory.markVariantAllInCart(this.pendingVariantId);
          } else if (inventory.isSoldOutMessage(rawMessage)) {
            inventory.markVariantOutOfStock(this.pendingVariantId);
          }

          this.showMessage(message);
          this.clearPendingState();
        }

        isPendingEvent(event) {
          if (!this.pendingVariantId || !event || event.source !== 'product-form') return false;

          return String(event.productVariantId) === String(this.pendingVariantId);
        }

        showMessage(message) {
          if (!this.message) return;

          this.message.textContent = message;
          this.message.hidden = false;
        }

        clearMessage() {
          if (!this.message) return;

          this.message.textContent = '';
          this.message.hidden = true;
        }

        clearPendingState() {
          this.pendingVariantId = null;
          this.pendingButton = null;
          this.pendingQuantity = 1;
        }

        onKeyDown(event) {
          if (event.key === 'Escape') this.close();
        }
      }
    );
  }

  const colorPicker = window.KronkCollectionColorPicker || {
    getPicker(swatch) {
      return swatch.closest('[data-kronk-color-picker]');
    },

    getTargets(swatch, picker) {
      const targets = [];
      const card = swatch.closest('.product-card-wrapper');
      const cardImage = card?.querySelector('[data-kronk-card-primary-image]');
      const sheetImage = picker?.querySelector('.kronk-mobile-quick-add__media img');

      if (cardImage) targets.push(cardImage);
      if (sheetImage) targets.push(sheetImage);

      return targets;
    },

    updateImage(swatch, picker) {
      const src = swatch.dataset.kronkImageSrc;
      if (!src) return;

      this.getTargets(swatch, picker).forEach((image) => {
        if (swatch.dataset.kronkImageSrcset) image.srcset = swatch.dataset.kronkImageSrcset;
        image.src = src;
        if (swatch.dataset.kronkImageAlt) image.alt = swatch.dataset.kronkImageAlt;
        if (swatch.dataset.kronkImageWidth) image.width = swatch.dataset.kronkImageWidth;
        if (swatch.dataset.kronkImageHeight) image.height = swatch.dataset.kronkImageHeight;
      });
    },

    select(swatch) {
      const picker = this.getPicker(swatch);
      if (!picker || swatch.disabled) return;

      const colorValue = swatch.dataset.kronkColorValue;

      picker.querySelectorAll('[data-kronk-color-swatch]').forEach((button) => {
        const isSelected = button === swatch;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });

      picker.querySelectorAll('[data-kronk-color-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.kronkColorPanel !== colorValue;
      });

      this.updateImage(swatch, picker);
    },

    initialize(root = document) {
      root.querySelectorAll('[data-kronk-color-picker]').forEach((picker) => {
        const selected = picker.querySelector('[data-kronk-color-swatch].is-selected:not([disabled])');
        if (selected) this.select(selected);
      });
    },
  };

  window.KronkCollectionColorPicker = colorPicker;

  if (!window.KronkCollectionColorPickerSubscribed) {
    window.KronkCollectionColorPickerSubscribed = true;

    document.addEventListener('click', (event) => {
      const swatch = event.target.closest('[data-kronk-color-swatch]');
      if (!swatch) return;

      event.preventDefault();
      colorPicker.select(swatch);
    });

    document.addEventListener('shopify:section:load', (event) => {
      colorPicker.initialize(event.target);
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => colorPicker.initialize());
    } else {
      colorPicker.initialize();
    }
  }
})();
